// Google Consent Mode v2 wiring for the SPA.
//
// The six non-security dimensions are listed individually in the customise
// view (the prompt asked for granular toggles, not grouped categories) so
// that anyone wanting to opt into analytics-only without ad personalisation
// can do exactly that.

export type ConsentSignal = 'granted' | 'denied';

export type ConsentDimensions = {
  ad_storage: ConsentSignal;
  ad_user_data: ConsentSignal;
  ad_personalization: ConsentSignal;
  analytics_storage: ConsentSignal;
  functionality_storage: ConsentSignal;
  personalization_storage: ConsentSignal;
  // Always granted. Includes auth + antiforgery cookies, which are
  // strictly necessary and exempt from PECR consent.
  security_storage: 'granted';
};

export type StoredConsent = {
  version: 1;
  decidedAt: string;
  consent: ConsentDimensions;
};

const STORAGE_KEY = 'gmt:consent:v1';

export const DENY_ALL: ConsentDimensions = {
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
  analytics_storage: 'denied',
  functionality_storage: 'denied',
  personalization_storage: 'denied',
  security_storage: 'granted',
};

export const GRANT_ALL: ConsentDimensions = {
  ad_storage: 'granted',
  ad_user_data: 'granted',
  ad_personalization: 'granted',
  analytics_storage: 'granted',
  functionality_storage: 'granted',
  personalization_storage: 'granted',
  security_storage: 'granted',
};

export const DIMENSION_KEYS: ReadonlyArray<keyof Omit<ConsentDimensions, 'security_storage'>> = [
  'analytics_storage',
  'functionality_storage',
  'personalization_storage',
  'ad_storage',
  'ad_user_data',
  'ad_personalization',
];

export const DIMENSION_LABELS: Record<typeof DIMENSION_KEYS[number], { title: string; blurb: string }> = {
  analytics_storage: {
    title: 'Analytics',
    blurb: 'How many people visit which pages, how the sweepstake flow performs. Helps us spot broken links and confusing screens.',
  },
  functionality_storage: {
    title: 'Functionality',
    blurb: 'Remembering small preferences (chosen language, dismissed banners) across visits.',
  },
  personalization_storage: {
    title: 'Personalisation',
    blurb: 'Tailoring the experience based on past activity. We do not currently use this but the slot exists for future features.',
  },
  ad_storage: {
    title: 'Advertising',
    blurb: 'Storage that supports ad tags. We do not run ads today, so granting this has no effect yet.',
  },
  ad_user_data: {
    title: 'Advertising — user data',
    blurb: 'Sharing user data with Google for ad measurement or audiences. Off by default, no effect until ads run.',
  },
  ad_personalization: {
    title: 'Advertising — personalisation',
    blurb: 'Tailoring ad content. Off by default, no effect until ads run.',
  },
};

export function loadConsent(): StoredConsent | null {
  try {
    const raw = window.localStorage?.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredConsent>;
    if (parsed.version !== 1 || !parsed.consent) return null;
    return parsed as StoredConsent;
  } catch {
    return null;
  }
}

export function saveConsent(consent: ConsentDimensions): StoredConsent {
  const payload: StoredConsent = {
    version: 1,
    decidedAt: new Date().toISOString(),
    consent,
  };
  try {
    window.localStorage?.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // localStorage unavailable (private mode, quota). The consent update
    // still fires for this page-load via pushConsentUpdate; the banner
    // will simply reappear on the next visit.
  }
  return payload;
}

type GtagShim = (...args: unknown[]) => void;

function gtag(): GtagShim {
  const w = window as typeof window & {
    dataLayer?: unknown[];
    gtag?: GtagShim;
  };
  if (w.gtag) return w.gtag;
  // Fallback for the (vanishingly rare) case where index.html's inline shim
  // was stripped: push directly to the queue so any later-loading GTM picks
  // it up.
  w.dataLayer = w.dataLayer || [];
  return (...args: unknown[]) => {
    w.dataLayer!.push(args);
  };
}

export function pushConsentUpdate(consent: ConsentDimensions): void {
  gtag()('consent', 'update', consent);
}
